import { Card, CardContent } from "@/components/ui/card"
import {
  formatDistance,
  formatDuration,
  formatCurrency,
  calculatePercentageDiff,
  calculateDistanceDiff,
  calculateTimeDiff,
  calculateEuroDiff,
} from "../../utils/formatUtils"
import { SummaryData } from "./DeliveryOrderReport"
import { useTranslation } from "react-i18next"

const ROW_LABELS = {
  actual: "orders.report.summary.rows.actual",
  expected: "orders.report.summary.rows.expected",
  approach: "orders.report.summary.rows.approach",
} as const

interface MetricRowData {
  label: (typeof ROW_LABELS)[keyof typeof ROW_LABELS]
  distance: string
  distanceChange: number | null
  distanceChangeAbsolute: string | null
  duration: string
  durationChange: number | null
  durationChangeAbsolute: string | null
  cost: string
  costChange: number | null
  costChangeAbsolute: string | null
}

function MetricCell({
  value,
  change,
  changeAbsolute,
}: {
  value: string
  change: number | null
  changeAbsolute: string | null
}) {
  if (!change) {
    return (
      <div className="flex flex-col text-right font-mono">
        <span className="font-medium text-foreground">{value}</span>
      </div>
    )
  }
  const changeColor =
    change > 0
      ? "text-red-500"
      : change < 0
        ? "text-green-500"
        : "text-foreground"
  const changeIcon = change > 0 ? "+" : "-"

  return (
    <div className="flex flex-col text-right font-mono">
      <span className={`${changeColor} text-sm font-semibold`}>
        {changeIcon} {changeAbsolute} ({changeIcon}
        {Math.abs(change).toFixed(2)}%)
      </span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  )
}

function transformApiData(data: SummaryData): MetricRowData[] {
  // Orders without expected tolls (external stop syncs such as GBOX) would
  // make every actual toll look like an overrun (+19466% at oskargroup):
  // compare only the actual tolls of the orders that have expected ones.
  const comparedActualTolls =
    data.total_actual_tolls_with_expected ?? data.total_actual_tolls
  // No order priced its planned route: nothing to compare with.
  const hasExpectedTolls =
    data.total_expected_tolls !== 0 || data.total_actual_tolls === 0

  return [
    {
      label: ROW_LABELS.actual,
      distance: formatDistance(data.total_actual_distance),
      distanceChange: calculatePercentageDiff(
        data.total_actual_distance,
        data.total_expected_distance,
      ),
      distanceChangeAbsolute: calculateDistanceDiff(
        data.total_actual_distance,
        data.total_expected_distance,
      ),
      duration: formatDuration(data.total_actual_duration),
      durationChange: calculatePercentageDiff(
        data.total_actual_duration,
        data.total_expected_duration,
      ),
      durationChangeAbsolute: calculateTimeDiff(
        data.total_actual_duration,
        data.total_expected_duration,
      ),
      cost: formatCurrency(data.total_actual_tolls),
      costChange: hasExpectedTolls
        ? calculatePercentageDiff(
            comparedActualTolls,
            data.total_expected_tolls,
          )
        : null,
      costChangeAbsolute: hasExpectedTolls
        ? calculateEuroDiff(comparedActualTolls, data.total_expected_tolls)
        : null,
    },
    {
      label: ROW_LABELS.expected,
      distance: formatDistance(data.total_expected_distance),
      distanceChange: null,
      distanceChangeAbsolute: null,
      duration: formatDuration(data.total_expected_duration),
      durationChange: null,
      durationChangeAbsolute: null,
      cost: hasExpectedTolls ? formatCurrency(data.total_expected_tolls) : "—",
      costChange: null,
      costChangeAbsolute: null,
    },
    {
      label: ROW_LABELS.approach,
      distance: formatDistance(data.total_actual_approach_distance),
      distanceChange: null,
      distanceChangeAbsolute: null,
      duration: formatDuration(data.total_actual_approach_duration),
      durationChange: null,
      durationChangeAbsolute: null,
      cost: formatCurrency(data.total_actual_approach_tolls),
      costChange: null,
      costChangeAbsolute: null,
    },
  ]
}

export function SummarySection({ data }: { data: SummaryData }) {
  const { t } = useTranslation()
  const displayData = transformApiData(data)

  return (
    <div className="mb-12">
      <h2 className="mb-4 text-xl font-semibold">
        {t("orders.report.summary.title")}
      </h2>
      <Card className="overflow-hidden py-0 shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-muted">
                  <th className="border-r p-2 text-left text-sm font-semibold text-muted-foreground"></th>
                  <th
                    colSpan={3}
                    className="border-r p-2 text-right text-sm font-semibold text-muted-foreground"
                  >
                    {t("orders.report.summary.distance")}
                  </th>
                  <th
                    colSpan={3}
                    className="border-r p-2 text-right text-sm font-semibold text-muted-foreground"
                  >
                    {t("orders.report.summary.duration")}
                  </th>
                  <th
                    colSpan={3}
                    className="p-2 text-right text-sm font-semibold text-muted-foreground"
                  >
                    {t("orders.report.summary.tolls")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((row) => (
                  <tr key={row.label} className="border-b hover:bg-muted/50">
                    <td className="border-r bg-muted p-2 text-sm font-medium">
                      {t(row.label)}
                    </td>
                    <td colSpan={3} className="border-r p-2">
                      <MetricCell
                        value={row.distance}
                        change={row.distanceChange}
                        changeAbsolute={row.distanceChangeAbsolute}
                      />
                    </td>
                    <td colSpan={3} className="border-r p-2">
                      <MetricCell
                        value={row.duration}
                        change={row.durationChange}
                        changeAbsolute={row.durationChangeAbsolute}
                      />
                    </td>
                    <td colSpan={3} className="p-2">
                      <MetricCell
                        value={row.cost}
                        change={row.costChange}
                        changeAbsolute={row.costChangeAbsolute}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
